import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HStack, VStack, Text, Input, Box } from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { collectFindMatches, applyReplacements, focusFindMatch, type FindMatch } from '../../core/commands/findReplace';

/**
 * FIND/REPLACE dialog (Faz 8.2, OCS find_replace.rs parity): find text,
 * step through matches (Next) with camera centering + selection, or replace
 * everywhere in one undo step (Replace All). Matching is case-insensitive;
 * locked layers are skipped.
 */
export function FindReplaceDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((state) => state.manageDialog === 'findReplace');
    const onClose = useAppStore((state) => state.closeManageDialog);
    const [findText, setFindText] = useState('');
    const [replaceText, setReplaceText] = useState('');
    const [matches, setMatches] = useState<FindMatch[]>([]);
    const [cursor, setCursor] = useState(0);
    const [status, setStatus] = useState('');

    const scan = (): FindMatch[] => {
        const found = collectFindMatches(findText);
        setMatches(found);
        setCursor(0);
        if (found.length === 0) setStatus(t('ui.cad.find.noMatches'));
        else {
            setStatus(t('ui.cad.find.matchOf', { index: 1, total: found.length, label: found[0].label }));
            focusFindMatch(found[0]);
        }
        return found;
    };

    const next = () => {
        const found = matches.length > 0 ? matches : scan();
        if (found.length === 0) return;
        const idx = matches.length > 0 ? (cursor + 1) % matches.length : 0;
        setCursor(idx);
        setStatus(t('ui.cad.find.matchOf', { index: idx + 1, total: found.length, label: found[idx].label }));
        focusFindMatch(found[idx]);
    };

    const replaceNext = () => {
        const found = matches.length > 0 ? matches : scan();
        if (found.length === 0) return;
        const idx = cursor % found.length;
        const changed = applyReplacements([found[idx]], findText, replaceText, 'FIND/REPLACE');
        if (changed === 0) {
            setStatus(t('ui.cad.find.alreadyReplaced'));
            return;
        }
        const remaining = collectFindMatches(findText);
        setMatches(remaining);
        setCursor(Math.min(idx, Math.max(0, remaining.length - 1)));
        setStatus(remaining.length > 0
            ? t('ui.cad.find.matchOf', { index: Math.min(idx + 1, remaining.length), total: remaining.length, label: remaining[Math.min(idx, remaining.length - 1)].label })
            : t('ui.cad.find.allReplaced'));
    };

    const replaceAll = () => {
        const found = matches.length > 0 ? matches : collectFindMatches(findText);
        if (found.length === 0) { setStatus(t('ui.cad.find.noMatches')); return; }
        const changed = applyReplacements(found, findText, replaceText, 'FIND/REPLACE ALL');
        setMatches([]);
        setCursor(0);
        setStatus(changed > 0
            ? t('ui.cad.find.replacedCount', { count: changed })
            : t('ui.cad.find.noMatches'));
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('ui.cad.find.title')}
            size="md"
            footer={<DialogButton variant="ghost" onClick={onClose}>{t('common.cancel')}</DialogButton>}
        >
            <VStack spacing={3} align="stretch">
                <HStack spacing={3}>
                    <Text w="90px" flexShrink={0} fontSize="sm" color="var(--color-text-secondary)">
                        {t('ui.cad.find.findWhat')}
                    </Text>
                    <Input
                        size="sm"
                        value={findText}
                        bg="var(--color-bg-primary)"
                        borderColor="var(--color-border)"
                        onChange={(e) => setFindText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') scan(); }}
                        placeholder={t('ui.cad.find.findPlaceholder')}
                    />
                    <DialogButton onClick={scan} isDisabled={!findText.trim()}>
                        {t('ui.cad.find.findNext')}
                    </DialogButton>
                </HStack>
                <HStack spacing={3}>
                    <Text w="90px" flexShrink={0} fontSize="sm" color="var(--color-text-secondary)">
                        {t('ui.cad.find.replaceWith')}
                    </Text>
                    <Input
                        size="sm"
                        value={replaceText}
                        bg="var(--color-bg-primary)"
                        borderColor="var(--color-border)"
                        onChange={(e) => setReplaceText(e.target.value)}
                        placeholder={t('ui.cad.find.replacePlaceholder')}
                    />
                </HStack>
                <HStack spacing={2} justify="flex-end">
                    <DialogButton onClick={next} isDisabled={!findText.trim()}>
                        {t('ui.cad.find.next')}
                    </DialogButton>
                    <DialogButton onClick={replaceNext} isDisabled={!findText.trim()}>
                        {t('ui.cad.find.replace')}
                    </DialogButton>
                    <DialogButton variant="primary" onClick={replaceAll} isDisabled={!findText.trim()}>
                        {t('ui.cad.find.replaceAll')}
                    </DialogButton>
                </HStack>
                <Box px={1} minH="24px">
                    <Text fontSize="xs" color="var(--color-text-secondary)">{status}</Text>
                </Box>
                <Text fontSize="xs" color="var(--color-text-secondary)">
                    {t('ui.cad.find.scopeHint')}
                </Text>
            </VStack>
        </AppDialog>
    );
}
